import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { LoadingState } from "./components/AsyncState";
import { AuthGate } from "./features/auth/AuthGate";
import { AuthProvider } from "./features/auth/AuthContext";
import { ApiError } from "./lib/api";
import { OverviewPage } from "./pages/OverviewPage";
import { TasksPage } from "./pages/TasksPage";
import { ControlCanvasPage } from "./pages/ControlCanvasPage";

const TaskDetailPage = lazy(() =>
  import("./pages/TaskDetailPage").then((module) => ({ default: module.TaskDetailPage }))
);
const loadResourcePage = () => import("./pages/ResourcePages");
const AgentsPage = lazy(() =>
  loadResourcePage().then((module) => ({ default: module.AgentsPage }))
);
const ApprovalsPage = lazy(() =>
  loadResourcePage().then((module) => ({ default: module.ApprovalsPage }))
);
const AuditPage = lazy(() => loadResourcePage().then((module) => ({ default: module.AuditPage })));
const DiscoveryPage = lazy(() =>
  loadResourcePage().then((module) => ({ default: module.DiscoveryPage }))
);
const ResourcesPage = lazy(() =>
  loadResourcePage().then((module) => ({ default: module.ResourcesPage }))
);
const UsagePage = lazy(() => loadResourcePage().then((module) => ({ default: module.UsagePage })));
const loadGovernancePage = () => import("./pages/GovernancePages");
const AccessPage = lazy(() =>
  loadGovernancePage().then((module) => ({ default: module.AccessPage }))
);
const PolicyPage = lazy(() =>
  loadGovernancePage().then((module) => ({ default: module.PolicyPage }))
);
const SettingsPage = lazy(() =>
  loadGovernancePage().then((module) => ({ default: module.SettingsPage }))
);
const TracesPage = lazy(() =>
  loadGovernancePage().then((module) => ({ default: module.TracesPage }))
);
const loadSupplyPage = () => import("./pages/SupplyPages");
const ChannelsPage = lazy(() =>
  loadSupplyPage().then((module) => ({ default: module.ChannelPoolPage }))
);
const PricingPage = lazy(() =>
  loadSupplyPage().then((module) => ({ default: module.PricingPage }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2
    }
  }
});

const router = createBrowserRouter([
  { path: "/", element: <ControlCanvasPage /> },
  {
    element: <AppShell />,
    children: [
      { path: "overview", element: <OverviewPage /> },
      { path: "tasks", element: <TasksPage /> },
      { path: "tasks/:taskId", element: <TaskDetailPage /> },
      { path: "approvals", element: <ApprovalsPage /> },
      { path: "resources", element: <ResourcesPage /> },
      { path: "agents", element: <AgentsPage /> },
      { path: "discovery", element: <DiscoveryPage /> },
      { path: "access", element: <AccessPage /> },
      { path: "policy", element: <PolicyPage /> },
      { path: "usage", element: <UsagePage /> },
      { path: "audit", element: <AuditPage /> },
      { path: "traces", element: <TracesPage /> },
      { path: "channels", element: <ChannelsPage /> },
      { path: "pricing", element: <PricingPage /> },
      { path: "settings", element: <SettingsPage /> }
    ]
  }
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthGate>
          <Suspense
            fallback={
              <main className="page">
                <LoadingState label="Loading view" />
              </main>
            }
          >
            <RouterProvider router={router} />
          </Suspense>
        </AuthGate>
      </AuthProvider>
    </QueryClientProvider>
  );
}
